/**
 * 週次レポートの表示の決まり（課題 #59 段階 4。2026-09-29 高尾さん決定）。
 *
 * - 見出しは「先週の振り返り（week_start〜week_end）」。期間は API の値をそのまま出す
 * - 本文が空の週は出さない（BE も返さないが、念のため FE でも除く）
 * - 記録した日数は分母（/7）を付けず、前の週とも比べない
 * - 「判定ではない」旨の注記は本文と別に付けない（本文の文末に必ず入るため）
 *
 * 週次は毎週月曜 7:00（日本時間）に、前の週（月〜日）の分が作られる。
 * ダッシュボードには、月曜から次の週の分が届くまで（週の終わりの日曜から 7 日間）だけ出す。
 */
import type { EmployeeWeeklyReport } from '../api/client';

/** ダッシュボードに「先週の振り返り」として出す日数（週の終わりの日曜の翌日から数えて） */
export const LAST_WEEK_CARD_DAYS = 7;

/** 'YYYY-MM-DD' を暦日の通し番号にする（時差の影響を受けないよう UTC の 0 時で数える）。読めなければ null */
function dayNumber(ymd: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(t) ? null : Math.round(t / 86_400_000);
}

function fromDayNumber(n: number): string {
  return new Date(n * 86_400_000).toISOString().slice(0, 10);
}

/** 端末の暦で今日の 'YYYY-MM-DD' */
export function localToday(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** 'YYYY-MM-DD' を「9/21」の形にする。読めなければそのまま返す */
export function formatMonthDay(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? `${Number(m[2])}/${Number(m[3])}` : ymd;
}

/** 期間を「9/21〜9/27」の形にする */
export function formatWeekRange(weekStart: string, weekEnd: string): string {
  return `${formatMonthDay(weekStart)}〜${formatMonthDay(weekEnd)}`;
}

/** 週の月曜から、その週の日曜を求める（API が week_end を返さないとき用） */
export function weekEndOf(weekStart: string): string {
  const n = dayNumber(weekStart);
  return n == null ? weekStart : fromDayNumber(n + 6);
}

/** 週の月曜を weeks 週ずらす（管理者の確認画面の「前の週」「次の週」） */
export function shiftWeek(weekStart: string, weeks: number): string {
  const n = dayNumber(weekStart);
  return n == null ? weekStart : fromDayNumber(n + weeks * 7);
}

/** 本文のある週だけを残す */
export function visibleWeeklyReports(items: readonly EmployeeWeeklyReport[]): EmployeeWeeklyReport[] {
  return items.filter((r) => typeof r.comment === 'string' && r.comment.trim() !== '');
}

/**
 * ダッシュボードに「先週の振り返り」として出すか。
 * 週の終わり（日曜）の翌日から 7 日間だけ出す。それより古い週を「先週」と呼ばないため。
 */
export function isLastWeekReport(report: Pick<EmployeeWeeklyReport, 'week_end'>, today: string): boolean {
  const end = dayNumber(report.week_end);
  const now = dayNumber(today);
  if (end == null || now == null) return false;
  const daysSince = now - end;
  return daysSince >= 1 && daysSince <= LAST_WEEK_CARD_DAYS;
}

/** 記録した日数を出すか（0 日や読めない値は出さない。記録の無い週は作られないため通常は 1 以上） */
export function showRecordingDays(days: unknown): days is number {
  return typeof days === 'number' && Number.isFinite(days) && days >= 1;
}
